// src/pages/weight-dispute/WeightDisputes.jsx
//
// Merchant-facing Weight Disputes LIST (route: /weight-disputes).
// A merchant lands here to see weight-variance charges raised against their shipments, filter
// them, and drill into any one to accept or dispute. OPEN rows are visually nudged because they
// carry an auto-accept deadline — the merchant needs to act before it lapses.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Download,
  Search,
  Loader2,
  AlertTriangle,
  Scale,
  RefreshCw,
  Clock,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Check,
  X,
  Upload,
} from "lucide-react";
import useAuth from "../../hooks/useAuth";
import BulkActionsUploadModal from "./BulkActionsUploadModal";
import {
  fetchSummary,
  fetchDisputes,
  downloadDisputesCsv,
  acceptDispute,
  REVISED_SINCE_VIEWED,
  errorCodeOf,
  errorMessageOf,
} from "./weightDisputeApi";
import {
  StatusPill,
  ChargeStatusPill,
  MoneyAmount,
  formatInr,
  formatGrams,
  formatSignedGrams,
  formatDateTime,
  STATUS_FILTER_OPTIONS,
  CopyIconButton,
} from "./wdUi.jsx";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

// Summary chips, in the order a merchant cares about (action first). Each maps to a status
// filter; `key` is the field we read off the summary payload (with statusCounts fallback).
// Ordered as the dispute lifecycle reads left→right: needs action → under review → outcomes.
const SUMMARY_CHIPS = [
  { key: "open", status: "OPEN", label: "Open (Action Needed)", tone: "orange" },
  { key: "merchantDisputed", status: "MERCHANT_DISPUTED", label: "Disputed", tone: "blue" },
  { key: "merchantAccepted", status: "MERCHANT_ACCEPTED", label: "Accepted", tone: "purple" },
  {
    key: "disputeAcceptedByWayuvega",
    status: "DISPUTE_ACCEPTED_BY_WAYUVEGA",
    label: "Dispute Upheld",
    tone: "emerald",
  },
  {
    key: "disputeRejectedByWayuvega",
    status: "DISPUTE_REJECTED_BY_WAYUVEGA",
    label: "Dispute Rejected",
    tone: "rose",
  },
  // Display-only (no status filter maps to it): accepted-by-timeout, a subset of Accepted.
  { key: "autoAccepted", status: null, label: "Auto Accepted", tone: "slate", displayOnly: true },
];

// Label + count share the tone; card gets a soft tinted fill with a matching border.
const CHIP_TONES = {
  orange: { card: "border-orange-200 bg-orange-50", label: "text-orange-700", count: "text-orange-800" },
  blue: { card: "border-blue-200 bg-blue-50/60", label: "text-blue-700", count: "text-blue-800" },
  purple: { card: "border-purple-200 bg-purple-50/60", label: "text-purple-700", count: "text-purple-800" },
  emerald: { card: "border-emerald-200 bg-emerald-50/70", label: "text-emerald-700", count: "text-emerald-800" },
  rose: { card: "border-rose-200 bg-rose-50/60", label: "text-rose-700", count: "text-rose-800" },
  slate: { card: "border-gray-200 bg-white", label: "text-gray-700", count: "text-gray-800" },
};
const CHIP_ACTIVE_RING = {
  orange: "ring-2 ring-orange-400 border-orange-400",
  blue: "ring-2 ring-blue-400 border-blue-400",
  purple: "ring-2 ring-purple-400 border-purple-400",
  emerald: "ring-2 ring-emerald-400 border-emerald-400",
  rose: "ring-2 ring-rose-400 border-rose-400",
  slate: "ring-2 ring-gray-400 border-gray-400",
};

// The backend summary is { countsByStatus: { OPEN: n, MERCHANT_ACCEPTED: n, ... }, totalCount }.
// Status-name keys are UPPERCASE and survive camelization untouched (no lowercase after "_").
const readSummaryCount = (summary, statusName) => summary?.countsByStatus?.[statusName] ?? 0;

const toDateTimeParam = (dateStr, endOfDay) => {
  if (!dateStr) return undefined;
  // <input type="date"> gives "YYYY-MM-DD"; widen to a full-day boundary in IST — the backend
  // binds OffsetDateTime, which REQUIRES an explicit offset.
  return endOfDay ? `${dateStr}T23:59:59+05:30` : `${dateStr}T00:00:00+05:30`;
};

export default function WeightDisputes() {
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  // Draft filter inputs (what the merchant is typing) vs the applied filters (what we query).
  const [draft, setDraft] = useState({
    status: "",
    awbNumber: "",
    orderReferenceNumber: "",
    fromDate: "",
    toDate: "",
  });
  const [applied, setApplied] = useState({
    status: "",
    awbNumber: "",
    orderReferenceNumber: "",
    fromDate: "",
    toDate: "",
  });

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Bulk-accept selection: a Set of disputeId for OPEN rows the merchant has ticked. It is
  // deliberately reset whenever the underlying result set can change (page/filter/refresh), so a
  // stale id from a previous page can never ride into a bulk action. See the effect below.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  // CSV-driven bulk actions (accept/dispute many AWBs at once) — independent of the row-tick
  // selection above; opened from the header action row.
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  const buildFilters = useCallback(
    (a, page, size) => ({
      // Backend paginates 0-based (skip = pageNumber*pageSize); the UI counts pages from 1.
      pageNumber: page - 1,
      pageSize: size,
      status: a.status || undefined,
      awbNumber: a.awbNumber || undefined,
      orderReferenceNumber: a.orderReferenceNumber || undefined,
      createdFromDateTime: toDateTimeParam(a.fromDate, false),
      createdToDateTime: toDateTimeParam(a.toDate, true),
    }),
    []
  );

  const loadList = useCallback(
    async ({ silent } = {}) => {
      const token = getAuthToken?.();
      silent ? setRefreshing(true) : setLoading(true);
      setError(null);
      try {
        const filters = buildFilters(applied, pageNumber, pageSize);
        const res = await fetchDisputes(token, filters);
        setRows(Array.isArray(res?.weightDisputes) ? res.weightDisputes : []);
        setTotalCount(res?.totalCount ?? res?.total ?? 0);
      } catch (err) {
        console.error("fetchDisputes error", err);
        setError(errorMessageOf(err));
        setRows([]);
        setTotalCount(0);
      } finally {
        silent ? setRefreshing(false) : setLoading(false);
      }
    },
    [applied, pageNumber, pageSize, buildFilters, getAuthToken]
  );

  const loadSummary = useCallback(async () => {
    const token = getAuthToken?.();
    try {
      const res = await fetchSummary(token);
      setSummary(res || {});
    } catch (err) {
      // Summary is a nice-to-have header; never let it block the table.
      console.warn("fetchSummary failed", err);
    }
  }, [getAuthToken]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Selection must never outlive the rows it points at. `loadList` replaces `rows` on every
  // path that can change what's on screen — page change, filter apply, refresh — so clearing
  // here keeps the one source of truth without sprinkling resets across each handler.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [rows]);

  const applyFilters = () => {
    setPageNumber(1);
    setApplied({ ...draft });
  };

  const resetFilters = () => {
    const blank = {
      status: "",
      awbNumber: "",
      orderReferenceNumber: "",
      fromDate: "",
      toDate: "",
    };
    setDraft(blank);
    setApplied(blank);
    setPageNumber(1);
  };

  // Clicking a summary chip is a one-tap status filter. Clicking the active chip clears it.
  const onChipClick = (status) => {
    const next = applied.status === status ? "" : status;
    setDraft((d) => ({ ...d, status: next }));
    setApplied((a) => ({ ...a, status: next }));
    setPageNumber(1);
  };

  const onExport = async () => {
    const token = getAuthToken?.();
    setExporting(true);
    setError(null);
    try {
      // Export honours the applied filters, but not pagination — merchant wants the whole set.
      const filters = buildFilters(applied, undefined, undefined);
      delete filters.pageNumber;
      delete filters.pageSize;
      await downloadDisputesCsv(token, filters);
    } catch (err) {
      console.error("export error", err);
      setError(errorMessageOf(err));
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
  const hasActiveFilters =
    applied.status ||
    applied.awbNumber ||
    applied.orderReferenceNumber ||
    applied.fromDate ||
    applied.toDate;

  const rangeStart = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const rangeEnd = Math.min(pageNumber * pageSize, totalCount);

  const summaryChips = useMemo(
    () =>
      SUMMARY_CHIPS.map((chip) => ({
        ...chip,
        count: chip.displayOnly ? (summary?.autoAcceptedCount ?? 0) : readSummaryCount(summary, chip.status),
        active: !chip.displayOnly && applied.status === chip.status,
      })),
    [summary, applied.status]
  );

  // ---- bulk-accept selection (OPEN rows only) ----
  // Only OPEN charges can be accepted; every other status renders a disabled cell and is
  // ineligible for select-all.
  const selectableRows = useMemo(() => rows.filter((d) => d.status === "OPEN"), [rows]);

  const selectedRows = useMemo(
    () => selectableRows.filter((d) => selectedIds.has(d.disputeId)),
    [selectableRows, selectedIds]
  );

  const selectedTotal = useMemo(
    () => selectedRows.reduce((sum, d) => sum + (Number(d.extraChargeAmount) || 0), 0),
    [selectedRows]
  );

  const allSelectableSelected =
    selectableRows.length > 0 && selectedRows.length === selectableRows.length;

  const toggleRow = useCallback((disputeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(disputeId)) next.delete(disputeId);
      else next.add(disputeId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      // Header toggle is all-or-nothing across the OPEN rows on the current page.
      if (prev.size >= selectableRows.length && selectableRows.length > 0) return new Set();
      return new Set(selectableRows.map((d) => d.disputeId));
    });
  }, [selectableRows]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-50 px-3 sm:px-5 pt-3 pb-10">
      <div className="w-full">
        {/* Header */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
              <Scale className="h-6 w-6 text-blue-600" />
              Weight Disputes
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Review weight-variance charges on your shipments. Accept a charge or raise a dispute
              before it auto-accepts.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadList({ silent: true })}
              disabled={refreshing || loading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => setBulkUploadOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              <Upload className="h-4 w-4" />
              Bulk actions
            </button>
            <button
              onClick={onExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export CSV
            </button>
          </div>
        </div>

        {/* Summary chips */}
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {summaryChips.map((chip) => {
            const tone = CHIP_TONES[chip.tone];
            return (
              <button
                key={chip.key}
                onClick={chip.displayOnly ? undefined : () => onChipClick(chip.status)}
                className={`flex flex-col items-start gap-1.5 rounded-2xl border px-5 py-4 text-left transition ${tone.card} ${
                  chip.active ? CHIP_ACTIVE_RING[chip.tone] : ""
                } ${chip.displayOnly ? "cursor-default" : "hover:shadow-md"}`}
                title={
                  chip.displayOnly
                    ? "Accepted automatically after the response window lapsed — included in the Accepted count"
                    : chip.active
                      ? "Click to clear this filter"
                      : `Filter by ${chip.label}`
                }
              >
                <span className={`text-[11px] font-bold uppercase tracking-wider sm:text-xs ${tone.label}`}>
                  {chip.label}
                </span>
                <span className={`text-3xl font-bold leading-none tabular-nums ${tone.count}`}>{chip.count}</span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div className="lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
              <select
                value={draft.status}
                onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              >
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">AWB number</label>
              <input
                value={draft.awbNumber}
                onChange={(e) => setDraft((d) => ({ ...d, awbNumber: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="Search AWB"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">Order ref</label>
              <input
                value={draft.orderReferenceNumber}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, orderReferenceNumber: e.target.value }))
                }
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="Search order ref"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">From</label>
              <input
                type="date"
                value={draft.fromDate}
                onChange={(e) => setDraft((d) => ({ ...d, fromDate: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">To</label>
              <input
                type="date"
                value={draft.toDate}
                onChange={(e) => setDraft((d) => ({ ...d, toDate: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-end gap-2 lg:col-span-1">
              <button
                onClick={applyFilters}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
              >
                <Search className="h-4 w-4" />
                Apply
              </button>
              {hasActiveFilters ? (
                <button
                  onClick={resetFilters}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                >
                  Reset
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error ? (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {/* Bulk-accept bar — appears once at least one OPEN row is selected. */}
        {selectedRows.length > 0 ? (
          <div className="sticky top-2 z-20 mb-3">
            <div className="flex flex-col gap-3 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-slate-50 px-4 py-3 shadow-md ring-1 ring-indigo-100 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-indigo-600 px-2 text-xs font-bold text-white tabular-nums">
                  {selectedRows.length}
                </span>
                <span className="font-medium text-slate-800">selected</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-600">
                  Total{" "}
                  <strong className="font-bold tabular-nums text-slate-900">
                    {formatInr(selectedTotal)}
                  </strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearSelection}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition hover:bg-white hover:text-slate-700"
                >
                  Clear
                </button>
                <button
                  onClick={() => setBulkOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                >
                  <Check className="h-4 w-4" />
                  Accept charges
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <colgroup>
                <col className="w-[4%]" />
                <col className="w-[22%]" />
                <col className="w-[16%]" />
                <col className="w-[11%]" />
                <col className="w-[7%]" />
                <col className="w-[14%]" />
                <col className="w-[10%]" />
                <col className="w-[16%]" />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all open charges on this page"
                      title={
                        selectableRows.length === 0
                          ? "No open charges to select on this page"
                          : allSelectableSelected
                            ? "Clear selection"
                            : "Select all open charges on this page"
                      }
                      checked={allSelectableSelected}
                      disabled={selectableRows.length === 0}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </th>
                  <Th>AWB / Order ref</Th>
                  <Th>Charged → Carrier</Th>
                  <Th className="text-right">Amount</Th>
                  <Th className="text-center">Rev</Th>
                  <Th>Status</Th>
                  <Th>Charge</Th>
                  <Th className="text-right">Updated</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <SkeletonRows />
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <Scale className="h-10 w-10 text-gray-300" />
                        <p className="text-sm font-medium">No weight disputes found</p>
                        <p className="text-xs">
                          {hasActiveFilters
                            ? "Try adjusting your filters."
                            : "You have no weight-variance charges right now."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((d) => {
                    const isOpen = d.status === "OPEN";
                    return (
                      <tr
                        key={d.disputeId}
                        onClick={() => navigate(`/weight-disputes/${d.disputeId}`)}
                        className={`cursor-pointer transition hover:bg-blue-50/60 ${
                          isOpen ? "border-l-4 border-amber-400 bg-amber-50/30" : ""
                        } ${selectedIds.has(d.disputeId) ? "bg-indigo-50/60" : ""}`}
                      >
                        {/* Select (OPEN only) */}
                        <td className="px-3 py-3 align-top">
                          {isOpen ? (
                            <input
                              type="checkbox"
                              aria-label={`Select charge ${d.awbNumber || d.disputeId}`}
                              checked={selectedIds.has(d.disputeId)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleRow(d.disputeId);
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          ) : (
                            <span className="block h-4 w-4" aria-hidden="true" />
                          )}
                        </td>

                        {/* AWB / order ref */}
                        <td className="px-3 py-3 align-top">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">
                              {d.awbNumber || "-"}
                            </span>
                            <CopyIconButton value={d.awbNumber} label="AWB" />
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                            <span className="truncate" title={d.orderReferenceNumber || undefined}>
                              {d.orderReferenceNumber || "-"}
                            </span>
                            <CopyIconButton value={d.orderReferenceNumber} label="Order ref" />
                            {d.orderReferenceNumber ? (
                              <Link
                                to={`/shipment-details/${d.orderReferenceNumber}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0 rounded p-0.5 text-gray-400 transition hover:bg-blue-50 hover:text-blue-600"
                                title="View order details"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            ) : null}
                          </div>
                          {isOpen && d.autoAcceptOnDateTime ? (
                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700">
                              <Clock className="h-3 w-3" />
                              Auto-accepts {formatDateTime(d.autoAcceptOnDateTime)}
                            </div>
                          ) : null}
                        </td>

                        {/* Weights */}
                        <td className="px-3 py-3 align-top">
                          <div className="whitespace-nowrap text-sm text-gray-800">
                            {formatGrams(d.declaredWeightSnapshot?.chargedWeightGrams)}
                            <span className="mx-1 text-gray-400">→</span>
                            <span className="font-medium">{formatGrams(d.carrierWeightGrams)}</span>
                          </div>
                          <div
                            className={`mt-0.5 text-xs font-medium ${
                              (d.varianceGrams ?? 0) > 0 ? "text-rose-600" : "text-gray-500"
                            }`}
                          >
                            {formatSignedGrams(d.varianceGrams)}
                          </div>
                        </td>

                        {/* Amount */}
                        <td className="whitespace-nowrap px-3 py-3 text-right align-top">
                          <MoneyAmount
                            amount={d.extraChargeAmount}
                            className="text-sm font-semibold"
                          />
                        </td>

                        {/* Revision */}
                        <td className="px-2 py-3 text-center align-top">
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                            Rev {d.revisionNumber ?? 1}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-3 align-top">
                          <StatusPill status={d.status} />
                        </td>

                        {/* Charge status */}
                        <td className="px-3 py-3 align-top">
                          <ChargeStatusPill chargeStatus={d.chargeStatus} />
                        </td>

                        {/* Updated */}
                        <td className="whitespace-nowrap px-3 py-3 text-right align-top">
                          <span className="text-xs text-gray-500">
                            {formatDateTime(d.updatedDateTime || d.createdDateTime)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:flex-row">
            <div className="text-xs text-gray-600">
              {totalCount > 0 ? (
                <>
                  Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of{" "}
                  <strong>{totalCount}</strong>
                </>
              ) : (
                "No results"
              )}
            </div>
            <div className="flex items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageNumber(1);
                }}
                className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1 || loading}
                  className="inline-flex items-center rounded-lg border border-gray-300 bg-white p-1.5 text-gray-700 transition hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-xs text-gray-600">
                  Page {pageNumber} of {totalPages}
                </span>
                <button
                  onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                  disabled={pageNumber >= totalPages || loading}
                  className="inline-flex items-center rounded-lg border border-gray-300 bg-white p-1.5 text-gray-700 transition hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk-accept confirm + results modal */}
      <AnimatePresence>
        {bulkOpen ? (
          <BulkAcceptModal
            rows={selectedRows}
            total={selectedTotal}
            getAuthToken={getAuthToken}
            onClose={() => setBulkOpen(false)}
            onDone={async () => {
              setBulkOpen(false);
              clearSelection();
              await Promise.all([loadList({ silent: true }), loadSummary()]);
            }}
          />
        ) : null}
      </AnimatePresence>

      {/* Bulk CSV upload (accept/dispute many AWBs) */}
      <AnimatePresence>
        {bulkUploadOpen ? (
          <BulkActionsUploadModal
            getAuthToken={getAuthToken}
            onClose={() => setBulkUploadOpen(false)}
            onDone={async () => {
              setBulkUploadOpen(false);
              await Promise.all([loadList({ silent: true }), loadSummary()]);
            }}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 ${className}`}
    >
      {children}
    </th>
  );
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <tr key={i} className="animate-pulse">
          <td colSpan={8} className="px-4 py-4">
            <div className="h-4 w-3/4 rounded bg-gray-200" />
          </td>
        </tr>
      ))}
    </>
  );
}

// ------------------------------------------------------------------ bulk accept modal

// Backdrop + card shell (mirrors the detail page's modal anatomy; kept local so this feature's
// list stays self-contained under src/pages/weight-dispute/).
function ModalShell({ children, onClose, maxWidth = "max-w-lg" }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 8 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 8 }}
        className={`w-full ${maxWidth} overflow-hidden rounded-2xl bg-white shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

const BULK_REVISED_REASON = "Charge was revised — review this dispute individually";

// Confirm → run → results, all in one modal. The run is a SEQUENTIAL loop (never Promise.all):
// each accept settles the merchant's wallet, so we go one at a time and surface live progress.
// Per-dispute outcomes are collected as accepted | revised | failed and shown after the run.
function BulkAcceptModal({ rows, total, getAuthToken, onClose, onDone }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0); // count attempted so far
  const [results, setResults] = useState([]); // [{ disputeId, awbNumber, outcome, reason }]

  const count = rows.length;

  const run = async () => {
    setRunning(true);
    setProgress(0);
    const token = getAuthToken?.();
    const collected = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      try {
        await acceptDispute(token, row.disputeId, row.revisionNumber ?? 1);
        collected.push({
          disputeId: row.disputeId,
          awbNumber: row.awbNumber,
          outcome: "accepted",
        });
      } catch (e) {
        if (errorCodeOf(e) === REVISED_SINCE_VIEWED) {
          collected.push({
            disputeId: row.disputeId,
            awbNumber: row.awbNumber,
            outcome: "revised",
            reason: BULK_REVISED_REASON,
          });
        } else {
          // Covers everything else, including a server-side status change surfacing as 409/422
          // on this single endpoint — no special casing beyond REVISED_SINCE_VIEWED.
          collected.push({
            disputeId: row.disputeId,
            awbNumber: row.awbNumber,
            outcome: "failed",
            reason: errorMessageOf(e),
          });
        }
      }
      setProgress(i + 1);
    }
    setResults(collected);
    setRunning(false);
    setDone(true);
  };

  const acceptedCount = results.filter((r) => r.outcome === "accepted").length;
  const notAccepted = results.filter((r) => r.outcome !== "accepted");

  // While running (or after), the only way out is the flow's own buttons — block backdrop close.
  const closeGuard = running ? () => {} : done ? onDone : onClose;

  return (
    <ModalShell onClose={closeGuard} maxWidth="max-w-lg">
      <ModalHeader title={done ? "Accept results" : "Accept charges"} onClose={closeGuard} />

      {!done ? (
        <>
          <div className="px-5 py-4">
            <p className="text-sm text-gray-600">
              You&apos;re accepting the weight-variance charges on{" "}
              <strong>{count}</strong> {count === 1 ? "shipment" : "shipments"}:
            </p>
            <div className="mt-3 rounded-xl bg-gray-50 p-4 text-center">
              <div className="text-xs uppercase tracking-wide text-gray-400">Total amount</div>
              <div className="mt-1 text-3xl font-bold tabular-nums text-gray-900">
                {formatInr(total)}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                across {count} {count === 1 ? "dispute" : "disputes"}
              </div>
            </div>

            <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={running}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-xs text-gray-700">
                I understand <strong className="tabular-nums">{formatInr(total)}</strong> in total
                will be settled from my wallet across {count}{" "}
                {count === 1 ? "dispute" : "disputes"} and this can&apos;t be undone.
              </span>
            </label>

            {running ? (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-indigo-50 p-2.5 text-sm text-indigo-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                Accepting {progress} / {count}…
              </div>
            ) : null}
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
            <button
              onClick={onClose}
              disabled={running}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={run}
              disabled={running || !acknowledged}
              title={!acknowledged ? "Tick the acknowledgment above to continue" : undefined}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {running ? "Accepting…" : `Accept ${count} ${count === 1 ? "charge" : "charges"}`}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="px-5 py-4">
            <div
              className={`rounded-xl border p-4 ${
                acceptedCount === count
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-amber-200 bg-amber-50"
              }`}
            >
              <div className="flex items-center gap-2">
                {acceptedCount === count ? (
                  <Check className="h-5 w-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                )}
                <p className="text-sm font-semibold text-gray-900">
                  Accepted {acceptedCount} of {count}
                </p>
              </div>
              {notAccepted.length > 0 ? (
                <p className="mt-1 text-xs text-gray-600">
                  {notAccepted.length}{" "}
                  {notAccepted.length === 1 ? "charge" : "charges"} couldn&apos;t be accepted —
                  see below.
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-600">
                  All selected charges were settled from your wallet.
                </p>
              )}
            </div>

            {notAccepted.length > 0 ? (
              <ul className="mt-3 max-h-64 space-y-2 overflow-y-auto">
                {notAccepted.map((r) => (
                  <li
                    key={r.disputeId}
                    className="rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-sm font-semibold text-gray-900">
                        {r.awbNumber || "-"}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          r.outcome === "revised"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {r.outcome === "revised" ? "Revised" : "Failed"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-600">{r.reason}</p>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
            <button
              onClick={onDone}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Done
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}
